import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { useMediaQuery } from 'react-responsive';
import ActionHeader from './header';
import TeamsCheckIcon from '../../../assets/icons/teams-check.svg';
import { draftStore, sponsorsStore } from '../../../store';
import { Row, Col, Wrapper } from '../../../components/layout';
import { SvgIconContainer } from '../../../components/icons';
import { pickPlayer, setStep, restartDraft } from '../../../actions/status';
import PlayersContainer from './playersContainer';
import DraftSummary from './draftSummary';
import useInterval from '../../../lib/hooks/useInterval';
import {
    mobileBreakpoint,
    getFormattedPlayer,
    getAvailablePlayers,
    getRandomPlayerInRange,
    getCurrentRound
} from '../../../lib';
import LoadingIndicator from '../../../components/loading';
import { fetchSuccess } from '../../../actions/players';
import { setDraftSpeed } from '../../../actions/options';
import { ExitDraftModal, RestartDraftModal } from '../../../components/modal';
import { TabsWhenMobile } from '../../../components';
import useFetchUrl from '../../../lib/hooks/useFetchUrl';
// import useGetUserData from '../../../lib/hooks/useGetUserData';

const RunningDraftWrapper = styled(Wrapper)`
    margin-top: 0;
    padding-top: 0px;
    @media(max-width: 425px){
        padding: 0 15px;
    }
`;

const TeamScrollList = styled.div`
    display: flex;
    position: relative;
    left: ${props => `-${props.leftOffset}px`};
    margin-top: 40px;
    margin-bottom: 40px;
    @media(max-width: 900px){
        margin-top: 0px;
    }
`

const BoldText = styled.span`
    font-weight: bold;
    margin-right: 2px;
`

const TeamSquareContainer = styled.div`
    position: relative;
    min-width: ${props => props.isPicking ? '90px' : '80px'};
    height: ${props => props.isPicking ? '87px' : '77px'};
    margin-top: ${props => props.isPicking ? '-10px' : '0px'};
    background-color: #161718;
    margin-right: 10px;
    border-bottom: ${props => props.isPicking ? '3px solid #d6001c' : '3px solid transparent'};
`

const PickNumber = styled.span`
    display: flex;
    justify-content: flex-end;
    color: #eaebec;
    margin-right: 5px;
    padding-top: 2px;
    font-weight: 700;
    font-size: 12px;
    line-height: 14px;
`;
const TeamLogo = styled.img.attrs(props => ({
    src: props.src
}))`
    display: flex;
    width: 40px;
    height: 40px;
    margin: 5px auto 0px;
`;

const FirstColumn = styled(Col)`
    flex: 0 0 69%;
    align-self: flex-start;
    flex-wrap: wrap;
    @media(max-width: 900px){
        flex: 0 0 100%;
    }
`;

const SecondColumn = styled(Col)`
    flex: 0 0 31%;
    align-self: flex-start;
    flex-wrap: wrap;
    @media(max-width: 900px){
        flex: 0 0 100%;
    }
`;

const TeamsCheckIconContainer = styled.img.attrs(props => ({
    src: props.src
}))`
    margin-right: 5px;
`;

const TeamsCount = styled.p`
    font-size: 14px;
    text-transform: uppercase;
    font-weight: 700;
`;

// const ForfeitLabel = styled.span`
//     display: block;
//     text-align: center;
//     font-size: 12px;
//     padding-top: 3px;
// `;

function TeamSquare({ logo, position, isPicking, isSelected }){
    return (
        <TeamSquareContainer isPicking={isPicking}>
            <SvgIconContainer show={isSelected}>
                <svg width="31px" height="31px" viewBox="0 0 31 31" version="1.1">
                    <path d="M0 0L31 0L0 31L0 0Z" id="Rectangle" fill="#D6001C" stroke="none" />
                    <path d="M0 3.09627L2.60266 6L8 0" transform="translate(6 6)" id="Path-2" fill="none" stroke="#FFFFFF" strokeWidth="2" />
                </svg>
            </SvgIconContainer>
            <PickNumber>{position}</PickNumber>
            <TeamLogo src={logo}/>
        </TeamSquareContainer>
    );
}

function RunningDraft(){
    const [ isExitModalOpen, setExitModalOpen ] = useState(false);
    const [ isRestartModalOpen, setRestartModalOpen ] = useState(false);
    const [ activeTab, setActiveTab ] = useState(0);
    const { state, dispatch } = useContext(draftStore);
    const { state: { mockDraft } } = useContext(sponsorsStore);

    const { 
        status:{ draftOrder, currentPickNumber, selectedPlayers, running },
        options:{ totalRounds, speed, selectedTeams, year, playerPostfix, rankings },
        roundPick: { data: roundPickRanges }
    } = state;

    const [ players, loading ] = useFetchUrl(`players/fc/players-${year}${playerPostfix}.json`, fetchSuccess);
    const isMobile = useMediaQuery({ maxWidth: mobileBreakpoint });
    // const currentRound = Math.ceil(currentPickNumber/ teams.length);
    const currentRound = getCurrentRound(roundPickRanges, currentPickNumber);
    const listLeftOffset = currentPickNumber > 1 ? ((currentPickNumber - 1) *  90) : 0;
    const isMyTurn = running && selectedTeams.some((code) => code === draftOrder[currentPickNumber - 1].code);
    const availablePlayers = getAvailablePlayers(players, selectedPlayers);
    const pauseDraft = isMyTurn || !running || loading || isExitModalOpen || isRestartModalOpen;

    const handlePickPlayer = (playerInfo) => {
        const pickedPlayer = getFormattedPlayer(playerInfo, draftOrder[currentPickNumber - 1], currentRound, currentPickNumber, isMyTurn);
        dispatch(pickPlayer(currentPickNumber, totalRounds, roundPickRanges, pickedPlayer));
    }

    useInterval(() => {
        const randomPlayerInRange = getRandomPlayerInRange(availablePlayers, rankings);
        handlePickPlayer(randomPlayerInRange);
    }, pauseDraft ? null : speed);

    if(!running) dispatch(setStep(4));
    return( 
    <RunningDraftWrapper>
        <ExitDraftModal
            isOpen={isExitModalOpen}
            handleModalOpen={setExitModalOpen}
        />
        <RestartDraftModal
            isOpen={isRestartModalOpen}
            handleModalOpen={setRestartModalOpen}
            dispatch={dispatch}
            restartDraft={restartDraft}
        />
        <ActionHeader
            heading="Mock Draft"
            subHeading={<>Draft Year <b>{year}</b></>}
            speed={speed}
            setDraftSpeed={(speed) => dispatch(setDraftSpeed(speed))}
            openExitModal={() => setExitModalOpen(true)}
            openRestartModal={() => setRestartModalOpen(true)}
            sponsorLogo={mockDraft.url}
        />
        {!isMobile && <Row margin="20px 0 0 0" justifyContent="space-between">
            <Col>
                {running ? <><BoldText>Round {currentRound} </BoldText> of { totalRounds }</> : <BoldText>Draft Finished</BoldText>}
            </Col>
            <Col alignItems="center">
                <TeamsCheckIconContainer src={TeamsCheckIcon} />
                <TeamsCount>{`${selectedTeams.length} Team${selectedTeams.length > 1 ? 's' : ''}`}</TeamsCount>
            </Col>
        </Row>}

        {loading ? <LoadingIndicator/> :
        <>
            <TeamScrollList leftOffset={listLeftOffset}>
                {draftOrder.map((teamInfo, id) => {
                    const isSelected = selectedTeams.some((code) => code === teamInfo.code);
                    return <TeamSquare
                                key={`${teamInfo.code}-${id}`}
                                logo={teamInfo.logo}
                                position={id + 1}
                                isPicking={ id + 1 === currentPickNumber}
                                isSelected={isSelected}
                            />;
                })}
            </TeamScrollList>
            <TabsWhenMobile setActiveTab={setActiveTab} activeTab={activeTab} data={["Players", "Draft Summary"]}>
                <FirstColumn>
                    <PlayersContainer players={players} selectedPlayers={selectedPlayers} handleAddPlayer={handlePickPlayer} isMyTurn={isMyTurn} />
                </FirstColumn>
                <SecondColumn>
                    <DraftSummary totalRounds={totalRounds} selectedPlayers={selectedPlayers} currentRound={currentRound} />
                </SecondColumn>
            </TabsWhenMobile>
        </>
        }
    </RunningDraftWrapper>);
}

export default RunningDraft;